// consent.ts — versions, links, and plain-English copy for the in-app consent gate.
//
// Bump a version here when you change that wording. The consents log records the new
// version against every decision made from then on, so the record stays defensible
// (UK GDPR Art 7). The value is the "as published" date.
//
// NOTE what a bump does and does not do. TERMS are compared: App.tsx gates on
// `termsAcceptedVersion !== CONSENT_VERSIONS.terms`, so bumping terms genuinely re-asks
// everyone. RESEARCH is NOT compared — the gate tests `researchConsent === 'unset'` — so
// bumping research records the new wording for future decisions without re-prompting
// anyone who has already answered. That asymmetry is deliberate; see below.
export const CONSENT_VERSIONS = {
  terms: '2026-06-15',
  // 2026-08-06: added the Ask ReNurture free-text questions, and the fact that they
  // cannot be withdrawn individually.
  //
  // Existing consenters are deliberately NOT re-prompted. The research consent already
  // authorises PID-linked wellbeing measures: special category data, pseudonymous, and
  // therefore re-identifiable by whoever holds the PID mapping. The Ask questions are an
  // addition to that but a LESS intrusive one, being anonymous rather than pseudonymous
  // and attributable to nobody. Adding data that is strictly less identifying than what
  // was already agreed to sits within the scope of the consent given. See the DPIA §2.
  research: '2026-08-06',
} as const;

// Reuse the URLs already proven in Settings; the website serves www and apex.
export const TERMS_URL = 'https://www.renurture.org/terms';
export const PRIVACY_URL = 'https://www.renurture.org/privacy';
export const RESEARCH_NOTICE_URL = 'https://www.renurture.org/research-privacy.html';

// The research statements, rewritten free of the old Thinkific wording for the app
// (answers are kept in the app, encrypted in transit and at rest, with the most
// sensitive content on the device). Kept short; the full notice is one tap away.
export const RESEARCH_POINTS: readonly string[] = [
  'A few short, gentle wellbeing questions as you move through the programme, at your own pace. A couple of minutes each, and never more than once a month for the longer ones. They are not tests, and there are no wrong answers.',
  'Your answers are kept securely in the app, encrypted in transit and at rest, with the most sensitive content kept on your own device.',
  // Ask ReNurture capture. Two separate points on purpose: what is kept, and the one
  // real cost of keeping it without any identifier. Both must be read before consenting.
  'The questions you type into Ask ReNurture, the in-app guide, may also be kept for the research, so we can see what people need help with and whether the guide points them somewhere useful. They are stored encrypted, with no name, account or any other identifier attached, alongside the rating you give the answer if you give one.',
  'Because those questions are stored with nothing that identifies you, we cannot find yours again later to remove them one by one. You can stop taking part whenever you like, and nothing more is kept from that moment on.',
  'To study whether the programme helps, we make an anonymised copy with anything that could identify you removed.',
  'We may share that anonymised copy with an academic partner who helps us evaluate the programme. They only ever see anonymised data.',
  'We keep the research data for the evaluation, and for 10 years after any findings are published, then securely delete it.',
  'Taking part is completely voluntary. You can stop at any time, without a reason, and it never affects your access to the programme.',
];
